import { firstValueFrom, type Observable } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import type { WaitForAuthResolutionFn } from '@/features/iam'
import type { Workspace } from '@/features/iam/workspaces/Workspace'
import { fromAbortSignal } from '@/utils/rx-util'

/**
 * Options for {@link createAuthResolutionWaiter}.
 */
export interface CreateAuthResolutionWaiterOptions {
  /**
   * Emits the currently selected workspace (if any) once we've reached
   * that part of the flow.
   */
  currentWorkspace$: Observable<Workspace | null>

  /**
   * Emits `true` or `false` once we've made an auth decision.
   */
  isAuthenticated$: Observable<boolean>
}

/**
 * Creates an auth resolution waiter function.
 */
export function createAuthResolutionWaiter({
  currentWorkspace$,
  isAuthenticated$,
}: CreateAuthResolutionWaiterOptions): WaitForAuthResolutionFn {
  return async (signal) => {
    const signal$ = fromAbortSignal(signal)
    const authenticated = await firstValueFrom(
      isAuthenticated$.pipe(takeUntil(signal$)),
      { defaultValue: false },
    )

    if (!authenticated) {
      return { authenticated: false, workspace: null }
    }

    const workspace = await firstValueFrom(
      currentWorkspace$.pipe(takeUntil(signal$)),
      { defaultValue: null },
    )

    return { authenticated, workspace }
  }
}
